import { useFetcher } from '@remix-run/react'
import { useTranslation } from 'react-i18next'

import { Form } from '~/helpers/form'
import { makeShopOwnPath } from '~/helpers/routes'
import { useOptionalSeller } from '~/hooks/data/use-seller'
import { useShop } from '~/hooks/data/use-shop'
import { patchShopSchema } from '~/schemas/shop'
import { Alert } from '~/ui'
import { TextEditable } from '~/ui/text-editable'
import { cn } from '~/utils/classnames'

const style =
  'tracking-tightest text-foreground w-full text-base font-medium @sm:text-xl'

export const ShopName = () => {
  const seller = useOptionalSeller()
  const shop = useShop()

  if (seller) {
    return <ShopNameEditable />
  }

  return <h1 className={style}>{shop.name}</h1>
}

const ShopNameEditable = () => {
  const { t } = useTranslation()

  const shop = useShop()
  const fetcher = useFetcher()

  const isLoading = fetcher.state === 'submitting'
  const fetcherData = fetcher.data as {
    error?: string
    errors?: string[]
  } | null
  const hasApiError =
    fetcherData?.error || (fetcherData?.errors?.length ?? 0) > 0

  return (
    <Form
      action={makeShopOwnPath(shop, '/settings?index')}
      method="PATCH"
      schema={patchShopSchema.pick({ name: true }).required()}
      fetcher={fetcher}
      mode="onChange"
      values={{
        name: shop.name,
      }}
    >
      {({ Field, register, submit, getFieldState }) => {
        const fieldState = getFieldState('name')

        const errorMessage = hasApiError
          ? t('errors.something_went_wrong')
          : fieldState.error?.message
            ? t(fieldState.error?.message)
            : undefined

        return (
          <Field name="name" className="w-full @lg:max-w-2/3">
            {() => (
              <div className="flex flex-col">
                <TextEditable>
                  <TextEditable.Textarea
                    className={cn(style, isLoading && 'animate-pulse')}
                    maxLength={50}
                    {...register('name')}
                    preventLineBreaks
                    invalid={!!errorMessage}
                    onBlur={submit}
                    placeholder={t('input.shop_name.placeholder')!}
                  />
                </TextEditable>
                {errorMessage && (
                  <Alert type="error" className="my-1" title={errorMessage} />
                )}
              </div>
            )}
          </Field>
        )
      }}
    </Form>
  )
}
